import React, { useState, useMemo } from 'react';
import { useChartContainerWidth } from '../../../../hooks/useChartContainerWidth';
import './DailyTrendChartWeight.css';

const DailyTrendChartWeight = ({ measurements }) => {
    // Calculate daily averages for chart
    const chartData = useMemo(() => {
        if (!measurements || measurements.length === 0) return { dates: [], weightData: [], measurementDetails: {} };

        // Group measurements by date and store details for tooltips
        const dailyData = {};

        measurements.forEach(m => {
            const date = m.measurement_date || m.measure_new_date_time?.split('T')[0] || m.created_at?.split('T')[0];
            const time = m.measurement_time ||
                m.measure_new_date_time?.split('T')[1]?.slice(0, 5) ||
                m.measure_date_time?.split(' ')[1]?.slice(0, 5) ||
                m.created_at?.split('T')[1]?.slice(0, 5) ||
                '';

            if (!dailyData[date]) {
                dailyData[date] = { weight: [], measurements: [] };
            }

            if (m.weight != null) {
                const val = parseFloat(m.weight);
                dailyData[date].weight.push(val);
                dailyData[date].measurements.push({
                    date,
                    time,
                    weight: val,
                });
            }
        });

        // Calculate averages and sort by date
        const sortedDates = Object.keys(dailyData).sort((a, b) => new Date(a) - new Date(b));
        const weightData = sortedDates.map(date => {
            const values = dailyData[date].weight;
            return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
        });

        return { dates: sortedDates, weightData, measurementDetails: dailyData };
    }, [measurements]);

    // Determine dynamic Y-axis min/max with some padding
    const yDomain = useMemo(() => {
        const validWeights = chartData.weightData.filter(v => v != null);
        if (validWeights.length === 0) return { min: 40, max: 120, step: 10 }; // Safe defaults if no data

        let rawMin = Math.min(...validWeights);
        let rawMax = Math.max(...validWeights);

        if (rawMin === rawMax) {
            rawMin -= 1;
            rawMax += 1;
        }

        const padding = (rawMax - rawMin) * 0.2; // 20% padding
        const min = Math.max(0, Math.floor(rawMin - padding));
        let max = Math.ceil(rawMax + padding);

        // Calculate a nice step size
        const range = max - min;
        let step = range > 10 ? Math.ceil(range / 5) : (range > 5 ? 2 : (range <= 2 ? 0.25 : 1));

        // Adjust max to be a multiple of step
        max = min + (step * 5); // 5 grid sections

        return { min, max, step };
    }, [chartData]);


    // Chart dimensions and scaling
    const [chartGridRef, chartWidth] = useChartContainerWidth(480, 800);
    const chartHeight = 250;
    const padding = { top: 20, right: 20, bottom: 40, left: 60 };
    const plotWidth = chartWidth - padding.left - padding.right;
    const plotHeight = chartHeight - padding.top - padding.bottom;

    // Scale functions
    const scaleX = (index) => {
        if (chartData.dates.length <= 1) return padding.left + (plotWidth / 2); // Center if only 1
        return padding.left + (index / (chartData.dates.length - 1)) * plotWidth;
    };

    const scaleY = (value) => {
        if (value == null) return null;
        return padding.top + plotHeight - ((value - yDomain.min) / (yDomain.max - yDomain.min)) * plotHeight;
    };

    // Generate SVG path for line
    const generatePath = (data) => {
        const points = data
            .map((value, index) => {
                const y = scaleY(value);
                if (y == null) return null;
                return { x: scaleX(index), y };
            })
            .filter(p => p != null);

        if (points.length === 0) return '';

        let path = `M ${points[0].x} ${points[0].y}`;
        for (let i = 1; i < points.length; i++) {
            path += ` L ${points[i].x} ${points[i].y}`;
        }
        return path;
    };

    // Format date for x-axis
    const formatChartDate = (dateStr) => {
        const date = new Date(dateStr);
        return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
    };

    // Y-axis ticks
    const yTicks = Array.from({ length: 6 }, (_, i) => yDomain.min + (yDomain.step * i)).reverse();

    // Tooltip state
    const [tooltip, setTooltip] = useState(null);

    return (
        <div className="dtcw-chart-section">
            <h4 className="dtcw-title">Daily Trend Chart of Weight</h4>

            <div className="dtcw-chart-container">
                <div className="dtcw-chart-title-center">Daily trend chart of weight</div>
                <div className="dtcw-chart-legend">
                    <div className="dtcw-legend-item">
                        <div className="dtcw-legend-line dtcw-legend-weight"></div>
                        <span>weight</span>
                    </div>
                </div>

                <div className="dtcw-chart-area">
                    {chartData.dates.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>No data available for the selected period</div>
                    ) : (
                        <div className="dtcw-chart-placeholder">
                            <div className="dtcw-y-axis">
                                {yTicks.map(tick => (
                                    <span key={tick}>{tick.toFixed(2)}</span>
                                ))}
                            </div>
                            <div className="dtcw-chart-grid" ref={chartGridRef}>
                                <svg
                                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                                    preserveAspectRatio="xMidYMid meet"
                                    className="dtcw-chart-svg"
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                                >
                                    {/* Grid lines */}
                                    {yTicks.map(value => {
                                        const y = scaleY(value);
                                        if (y == null) return null;
                                        return (
                                            <line
                                                key={`grid-${value}`}
                                                x1={padding.left}
                                                y1={y}
                                                x2={chartWidth - padding.right}
                                                y2={y}
                                                stroke="#efefef"
                                                strokeWidth="1"
                                            />
                                        );
                                    })}

                                    {/* Weight line (teal/blue) */}
                                    {chartData.weightData.some(v => v != null) && (
                                        <path
                                            d={generatePath(chartData.weightData)}
                                            fill="none"
                                            stroke="#4B8B9B"
                                            strokeWidth="2"
                                        />
                                    )}

                                    {/* Hover interaction areas & dots */}
                                    {chartData.dates.map((date, index) => {
                                        const weightValue = chartData.weightData[index];
                                        const x = scaleX(index);
                                        const measurements = chartData.measurementDetails[date]?.measurements || [];
                                        const firstMeasurement = measurements[0] || {};

                                        if (weightValue == null) return null;

                                        const handleMouseEnter = (e) => {
                                            const rect = e.currentTarget.closest('.dtcw-chart-grid')?.getBoundingClientRect();
                                            if (!rect) return;
                                            setTooltip({
                                                x: e.clientX - rect.left,
                                                y: e.clientY - rect.top,
                                                date,
                                                weightValue,
                                                time: firstMeasurement.time || ''
                                            });
                                        };

                                        const handleMouseLeave = () => setTooltip(null);

                                        return (
                                            <g key={`points-${date}`}>
                                                {/* Invisible thicker line for easier hover */}
                                                <line
                                                    x1={x}
                                                    y1={padding.top}
                                                    x2={x}
                                                    y2={chartHeight - padding.bottom}
                                                    stroke="transparent"
                                                    strokeWidth="20"
                                                    style={{ cursor: 'pointer' }}
                                                    onMouseEnter={handleMouseEnter}
                                                    onMouseLeave={handleMouseLeave}
                                                />
                                                {/* Hover visible point (only shown or highlighted via CSS ideally, but we'll show it directly for safety here if we want) */}
                                                {/* In the mockup, there are no visible dots on the line until hovered, or very small ones. Let's omit the circle to match the pure line look until hovered */}
                                            </g>
                                        );
                                    })}
                                </svg>

                                <div className="dtcw-x-axis">
                                    {chartData.dates.map((date, index) => {
                                        // Show evenly spaced labels roughly to avoid crowding
                                        const skip = Math.ceil(chartData.dates.length / 5) || 1;
                                        if (index % skip === 0 || index === chartData.dates.length - 1) {
                                            return <span key={date} style={{ left: `${(scaleX(index) / chartWidth) * 100}%` }}>{formatChartDate(date)}</span>;
                                        }
                                        return null;
                                    })}
                                </div>

                                {/* Tooltip */}
                                {tooltip && (
                                    <div
                                        className="dtcw-tooltip"
                                        style={{
                                            left: `${tooltip.x}px`,
                                            top: `${tooltip.y - 40}px`,
                                            transform: 'translateX(-50%)'
                                        }}
                                    >
                                        <div className="dtcw-tooltip-content">
                                            <div className="dtcw-tooltip-date">{formatChartDate(tooltip.date)}</div>
                                            <div className="dtcw-tooltip-item">
                                                <span className="dtcw-tooltip-value">{tooltip.weightValue.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DailyTrendChartWeight;
